import { Injectable } from '@angular/core';
import { Track } from '@app/core/models/Track';
import {
  TrackArchiveSuccess,
  TrackEditSuccess,
  TrackTagsSaveSuccess,
  TrackTagsSuccess,
} from '@app/library/states/tracks.actions';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  CloseCurrentPanel,
  OpenSpecificPanel,
  PANELS_KEYS,
  TryActionUnlessPanelIsBlocking,
} from '@radioking/shared/pannel-manager';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import {
  ForceStartEditMultipleTracks,
  ForceStartEditTrack,
  ResetEditingTracks,
  SetActionAfterEditTrack,
  StartEditMultipleTracks,
  StartEditTrack,
  TrackDetails,
} from './editing-track.actions';

export class EditingTrackStateModel {
  tracks: Track[];
  actionAfterEdit: any;
}

@State<EditingTrackStateModel>({
  name: 'editingTrack',
  defaults: {
    tracks: [],
    actionAfterEdit: null,
  },
})
@Injectable()
export class EditingTrackState {
  @Selector()
  static tracks(ctx: EditingTrackStateModel) {
    return ctx.tracks;
  }

  @Selector()
  static track(ctx: EditingTrackStateModel) {
    return ctx.tracks[0];
  }

  @Selector()
  static isEditing(ctx: EditingTrackStateModel) {
    return ctx.tracks.length > 0;
  }

  @Selector()
  static isEditingTrack(ctx: EditingTrackStateModel): (id: number) => boolean {
    return id => ctx.tracks.filter(track => track.id === id).length > 0;
  }

  @Action(StartEditTrack)
  requestStartEdit(ctx: StateContext<EditingTrackStateModel>, { track }: StartEditTrack) {
    if (ctx.getState().tracks.length === 1 && track.id === ctx.getState().tracks[0].id) {
      return;
    }

    ctx.dispatch(
      new TryActionUnlessPanelIsBlocking(
        new OpenSpecificPanel(
          PANELS_KEYS.EDIT_TRACK_INFO,
          new ForceStartEditTrack(track),
        ),
      ),
    );
  }

  @Action(TrackDetails)
  trackDetails(ctx: StateContext<TrackDetails>, { track }: TrackDetails) {
    ctx.dispatch(
      new OpenSpecificPanel(PANELS_KEYS.TRACK_DETAILS, new ForceStartEditTrack(track)),
    );
  }

  @Action(TrackTagsSuccess)
  trackTagsSuccess(
    ctx: StateContext<EditingTrackStateModel>,
    { track }: TrackTagsSuccess,
  ) {
    ctx.patchState({
      tracks: [track],
    });
  }

  @Action(StartEditMultipleTracks)
  editMultipleTracks(
    ctx: StateContext<EditingTrackStateModel>,
    { tracks }: StartEditMultipleTracks,
  ) {
    ctx.dispatch(
      new TryActionUnlessPanelIsBlocking(
        new OpenSpecificPanel(
          PANELS_KEYS.EDIT_TRACK_INFO,
          new ForceStartEditMultipleTracks(tracks),
        ),
      ),
    );
  }

  @Action(ForceStartEditTrack)
  forceEditTrack(
    ctx: StateContext<EditingTrackStateModel>,
    { track }: ForceStartEditTrack,
  ) {
    ctx.patchState({
      tracks: [track],
    });
  }

  @Action(ForceStartEditMultipleTracks)
  forceEditMultipleTracks(
    ctx: StateContext<EditingTrackStateModel>,
    { tracks }: ForceStartEditMultipleTracks,
  ) {
    ctx.patchState({
      tracks,
    });
  }

  @Action([ResetEditingTracks, TrackEditSuccess, TrackTagsSaveSuccess])
  resetEditTracks(ctx: StateContext<EditingTrackStateModel>): Observable<void> | void {
    if (ctx.getState().actionAfterEdit) {
      return ctx.dispatch(new CloseCurrentPanel()).pipe(
        mergeMap(() => {
          const tmpAction = ctx.getState().actionAfterEdit;
          ctx.patchState({ actionAfterEdit: null });

          return ctx.dispatch(tmpAction);
        }),
      );
    } else {
      ctx.dispatch(new CloseCurrentPanel(true));
    }
    ctx.patchState({ tracks: [] });
  }

  @Action(TrackArchiveSuccess)
  closePanelIfNeeded(
    ctx: StateContext<EditingTrackStateModel>,
    { tracksId }: TrackArchiveSuccess,
  ) {
    const tracks = ctx.getState().tracks;
    const remainingTracksDeleted = tracks.filter(track => !tracksId.includes(track.id));
    if (tracks.length === 1 && tracksId.filter(id => id === tracks[0].id).length > 0) {
      ctx.dispatch(new CloseCurrentPanel(true));

      return;
    }
    if (remainingTracksDeleted.length === 1) {
      ctx.dispatch(new ForceStartEditTrack(remainingTracksDeleted[0]));

      return;
    }
  }

  @Action(SetActionAfterEditTrack)
  actionAfterEdit(
    ctx: StateContext<EditingTrackStateModel>,
    { action }: SetActionAfterEditTrack,
  ) {
    ctx.patchState({ actionAfterEdit: action });
  }
}
